import { all, fork, put, takeEvery, call } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/core';
import { mwErrorHandling } from '../../helpers/errors';

// apicore
import { APICore } from '../../helpers/api/apiCore';

// actions
import { clubsApiResponseSuccess, clubsApiResponseError } from './actions';

// constants
import { ClubActionTypes } from './constants';
import Club, { ClubExtraInfo } from './model';

interface ClubData {
    payload: Club;
    type: string;
}

interface ClubExtraInfoData {
    payload: ClubExtraInfo;
    type: string;
}

const api = new APICore();
const BASE_URL = '/clubs/';

/**
 * Logout the user
 */
function* getAll({ payload }: any): SagaIterator {
    try {
        const { includeExtraInfo } = payload;
        const response = yield call(api.get, { url: `${BASE_URL}${includeExtraInfo ? '?extra=true' : ''}` });
        yield put(clubsApiResponseSuccess(ClubActionTypes.GET_CLUBS, response.data));
    } catch (error: any) {
        const mwHandled = yield call(mwErrorHandling, error);
        if (!mwHandled) yield put(clubsApiResponseError(ClubActionTypes.GET_CLUBS, error));
    }
}

function* findOne({ payload }: { payload: { id: number; includeExtraInfo: boolean }; type: string }): SagaIterator {
    try {
        const { id, includeExtraInfo } = payload;
        const response = yield call(api.get, { url: `${BASE_URL}${id}${includeExtraInfo ? '?extra=true' : ''}` });
        yield put(clubsApiResponseSuccess(ClubActionTypes.FIND_CLUB, response.data));
    } catch (error: any) {
        const mwHandled = yield call(mwErrorHandling, error);
        if (!mwHandled) yield put(clubsApiResponseError(ClubActionTypes.FIND_CLUB, error));
    }
}

function* createClub({ payload }: ClubData): SagaIterator {
    try {
        const { name, shortName, commercialName, address, CP, CIF, phone, email, city, state, active } = payload;
        const createFields = { name, shortName, commercialName, address, CP, CIF, phone, email, city, state, active };
        const response = yield call(api.post, { url: BASE_URL, data: createFields });
        const created = response.data;
        yield put(clubsApiResponseSuccess(ClubActionTypes.CREATE_CLUB, created));
    } catch (error: any) {
        const mwHandled = yield call(mwErrorHandling, error);
        if (!mwHandled) yield put(clubsApiResponseError(ClubActionTypes.CREATE_CLUB, error));
    }
}

function* updateClub({ payload }: ClubData): SagaIterator {
    try {
        const { name, shortName, commercialName, address, CP, CIF, phone, email, city, state, active } = payload;
        const updateFields = { name, shortName, commercialName, address, CP, CIF, phone, email, city, state, active };
        const response = yield call(api.update, { url: `${BASE_URL}${payload.id ?? 0}`, data: updateFields });
        const updated = response.data;
        yield put(clubsApiResponseSuccess(ClubActionTypes.UPDATE_CLUB, updated));
    } catch (error: any) {
        const mwHandled = yield call(mwErrorHandling, error);
        if (!mwHandled) yield put(clubsApiResponseError(ClubActionTypes.UPDATE_CLUB, error));
    }
}

function* updateClubExtraInfo({ payload }: ClubExtraInfoData): SagaIterator {
    try {
        const {
            id,
            clubId,
            playingVenueAddress,
            latitude,
            longitude,
            contactName,
            contactPhone,
            contactEmail,
            instagram,
            facebook,
            youtube,
            kidsTimetable,
            teenagersTimetable,
            adultsTimetable,
            improvementTimetable,
            tecnificacionTimetable,
            diversidadTimetable,
            otherTimetable,
            paymentDetails,
            logoImg,
            extra1Img,
            extra2Img,
        } = payload;
        const updateFields = {
            id,
            clubId,
            playingVenueAddress,
            latitude,
            longitude,
            contactName,
            contactPhone,
            contactEmail,
            instagram,
            facebook,
            youtube,
            kidsTimetable,
            teenagersTimetable,
            adultsTimetable,
            improvementTimetable,
            tecnificacionTimetable,
            diversidadTimetable,
            otherTimetable,
            paymentDetails,
        };
        const response = yield call(api.update, { url: `${BASE_URL}${payload.clubId ?? 0}/extra`, data: updateFields });
        const updated = response.data;

        if (response?.data?.id) {
            if (typeof logoImg !== 'undefined' && typeof logoImg !== 'string')
                yield call(api.postWithFile, {
                    url: `${BASE_URL}${response.data.id}/extra/logoImg`,
                    data: { avatar: logoImg },
                });
            if (typeof extra1Img !== 'undefined' && typeof extra1Img !== 'string')
                yield call(api.postWithFile, {
                    url: `${BASE_URL}${response.data.id}/extra/extra1Img`,
                    data: { avatar: extra1Img },
                });
            if (typeof extra2Img !== 'undefined' && typeof extra2Img !== 'string')
                yield call(api.postWithFile, {
                    url: `${BASE_URL}${response.data.id}/extra/extra2Img`,
                    data: { avatar: extra2Img },
                });
        }

        yield put(clubsApiResponseSuccess(ClubActionTypes.UPDATE_CLUB_EXTRA_INFO, updated));
    } catch (error: any) {
        const mwHandled = yield call(mwErrorHandling, error);
        if (!mwHandled) yield put(clubsApiResponseError(ClubActionTypes.UPDATE_CLUB_EXTRA_INFO, error));
    }
}

function* renewClub({ payload }: ClubData): SagaIterator {
    try {
        const response = yield call(api.post, { url: `${BASE_URL}${payload.id ?? 0}/renew`, data: {} });
        const updated = response.data;
        yield put(clubsApiResponseSuccess(ClubActionTypes.RENEW_CLUB, updated));
    } catch (error: any) {
        const mwHandled = yield call(mwErrorHandling, error);
        if (!mwHandled) yield put(clubsApiResponseError(ClubActionTypes.RENEW_CLUB, error));
    }
}

export function* watchGetAllClubs() {
    yield takeEvery(ClubActionTypes.GET_CLUBS, getAll);
}

export function* watchFindClub() {
    yield takeEvery(ClubActionTypes.FIND_CLUB, findOne);
}

export function* watchCreateClub() {
    yield takeEvery(ClubActionTypes.CREATE_CLUB, createClub);
}

export function* watchUpdateClub() {
    yield takeEvery(ClubActionTypes.UPDATE_CLUB, updateClub);
}

export function* watchUpdateClubExtraInfo() {
    yield takeEvery(ClubActionTypes.UPDATE_CLUB_EXTRA_INFO, updateClubExtraInfo);
}

export function* watchRenewClub() {
    yield takeEvery(ClubActionTypes.RENEW_CLUB, renewClub);
}

function* clubsSaga() {
    yield all([
        fork(watchGetAllClubs),
        fork(watchFindClub),
        fork(watchCreateClub),
        fork(watchUpdateClub),
        fork(watchRenewClub),
        fork(watchUpdateClubExtraInfo),
    ]);
}

export default clubsSaga;
